(() => {
  const Core = window.KultfrauDiscoveryCore;
  if (!Core || customElements.get('kf-discovery')) return;
  const storageKey = 'kf-discovery:v1';
  const defaults = () => ({category: '', color: '', size: '', available: true, mode: 'personalized'});
  const deadline = (promise, milliseconds = 15000) => {
    let timer;
    return Promise.race([promise, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Timeout')), milliseconds); })]).finally(() => clearTimeout(timer));
  };
  class KultfrauDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.products = [];
      this.wishlistItems = [];
      this.history = {};
      this.filters = defaults();
      this.recent = [];
      this.seed = String(crypto.getRandomValues(new Uint32Array(1))[0]);
      this.imageIndex = 0;
      this.busy = false;
      this.complete = false;
      this.catalogError = false;
      this.strings = JSON.parse(this.querySelector('[data-discovery-strings]').textContent);
      this.card = this.querySelector('.kd-card');
      this.photo = this.querySelector('.kd-photo');
      this.filterDialog = this.querySelector('.kd-filter-dialog');
      this.detailDialog = this.querySelector('.kd-detail-dialog');
      this.cartDialog = this.querySelector('.kd-cart-dialog');
      this.cartOutcomes = new Map();
      this.status = this.querySelector('.kd-status');
      this.analytics = window.KultfrauDiscoveryAnalytics?.create(this);
      this.restore();
      this.suppressNewsletter();
      this.trackPreviewBar();
      this.on(this, 'click', event => this.click(event));
      this.on(this.querySelector('[data-cart-variant]'), 'change', () => this.renderCartSelection());
      this.on(this.card, 'pointerdown', event => this.pointerDown(event));
      this.on(this.card, 'pointermove', event => this.pointerMove(event));
      this.on(this.card, 'pointerup', event => this.pointerUp(event));
      this.on(this.card, 'pointercancel', () => this.pointerCancel());
      this.on(this.card, 'lostpointercapture', () => this.pointerCancel());
      this.on(this.card, 'keydown', event => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.decide('skip', 'keyboard'); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.decide('like', 'keyboard'); }
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); this.changeImage(event.key === 'ArrowUp' ? -1 : 1); }
        if (event.key === 'Enter') this.openDetails();
      });
      this.on(this.photo, 'error', () => this.message(this.strings.image_error));
      this.on(this.photo, 'load', () => { this.photo.style.visibility = ''; });
      for (const dialog of [this.filterDialog, this.detailDialog, this.cartDialog]) {
        this.on(dialog, 'click', event => {
          if (event.target === dialog) {
            const rect = dialog.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
          }
        });
      }
      this.on(window, 'pageshow', event => { if (event.persisted) this.refreshWishlist().catch(() => {}); });
      this.on(window, 'focus', () => { if (this.app && !this.busy) this.refreshWishlist().catch(() => {}); });
      const initial = JSON.parse(this.querySelector('[data-discovery-catalog]').textContent);
      this.merge(initial.products);
      this.nextUrl = initial.next;
      this.complete = !initial.next;
      this.current = this.products.find(p => p.id === this.restoredCurrent && Core.matches(p, this.filters) && !this.history[p.id]);
      if (this.current) this.restoredCurrent = null;
      this.chooseNext(true);
      this.loadCatalog();
      this.initWishlist();
      this.updateLinks();
    }
    disconnectedCallback() {
      this.analytics?.destroy();
      this.controller?.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.returnAnimation?.cancel();
      this.exitAnimation?.cancel();
      this.newsletterObserver?.disconnect();
      this.previewTreeObserver?.disconnect();
      this.previewAttributeObserver?.disconnect();
      this.previewResizeObserver?.disconnect();
      if (this.previewFrame) cancelAnimationFrame(this.previewFrame);
      this.previewFrame = null;
      this.previewBar = null;
      this.initialized = false;
    }
    on(target, name, handler) { target.addEventListener(name, handler, {signal: this.controller.signal}); }
    analyticsContext() { try { return this.analytics?.context() || null; } catch { return null; } }
    track(type, product, fields = {}, context) {
      try { return this.analytics?.record(type, product, fields, context) || null; } catch { return null; }
    }
    trackView() { try { this.analytics?.view(); } catch {} }
    forgetAnalytics() {
      if (this.lastAction) { delete this.lastAction.analyticsEventId; delete this.lastAction.analyticsContext; }
    }
    trackPreviewBar() {
      const schedule = () => {
        if (this.previewFrame) return;
        this.previewFrame = requestAnimationFrame(() => { this.previewFrame = null; this.updatePreviewBar(); });
      };
      this.previewAttributeObserver = new MutationObserver(schedule);
      this.previewResizeObserver = new ResizeObserver(schedule);
      this.previewTreeObserver = new MutationObserver(schedule);
      this.previewTreeObserver.observe(document.body, {childList: true, subtree: true});
      this.on(window, 'resize', schedule);
      this.on(window, 'pageshow', schedule);
      if (window.visualViewport) this.on(window.visualViewport, 'resize', schedule);
      this.updatePreviewBar();
    }
    updatePreviewBar() {
      if (window.visualViewport && Math.abs(window.visualViewport.scale - 1) > .01) return;
      // The iframe itself fills the viewport; its clipped wrapper is the visible bar.
      const bar = document.querySelector('#PBarNextFrameWrapper');
      if (bar !== this.previewBar) {
        this.previewAttributeObserver.disconnect();
        this.previewResizeObserver.disconnect();
        this.previewBar = bar;
        if (bar) {
          this.previewAttributeObserver.observe(bar, {attributes: true, attributeFilter: ['style', 'class', 'hidden']});
          this.previewResizeObserver.observe(bar);
        }
      }
      let overlap = 0;
      if (bar) {
        const rect = bar.getBoundingClientRect(), css = getComputedStyle(bar), height = window.innerHeight;
        if (css.position === 'fixed' && css.display !== 'none' && css.visibility !== 'hidden' && Number(css.opacity) > 0 && rect.width > 0 && rect.height > 0 && rect.bottom >= height - 1 && rect.top < height) {
          overlap = Math.round(Math.min(rect.height, height - Math.max(0, rect.top)));
        }
      }
      const value = `${overlap}px`;
      if (this.style.getPropertyValue('--kd-preview-height') !== value) this.style.setProperty('--kd-preview-height', value);
    }
    suppressNewsletter() {
      const dismissed = new WeakSet();
      const closeForms = () => {
        for (const button of document.querySelectorAll('.klaviyo-close-form')) {
          if (!dismissed.has(button)) { dismissed.add(button); button.click(); }
        }
      };
      this.newsletterObserver = new MutationObserver(closeForms);
      this.newsletterObserver.observe(document.body, {childList: true, subtree: true});
      closeForms();
    }
    message(text, productId = null) {
      this.messageProductId = productId;
      this.status.textContent = text;
      this.querySelector('.kd-detail-status').textContent = this.detailDialog.open ? text : '';
    }
    restore() {
      try {
        const state = JSON.parse(localStorage.getItem(storageKey));
        if (!state || typeof state !== 'object') return;
        if (state.history && !Array.isArray(state.history) && typeof state.history === 'object') this.history = Object.fromEntries(Object.entries(state.history).filter(([id, value]) => /^\d+$/.test(id) && ['like','skip'].includes(value?.kind)));
        if (typeof state.seed === 'string') this.seed = state.seed;
        if (state.filters && typeof state.filters === 'object') {
          this.filters = defaults();
          if (typeof state.filters.category === 'string') this.filters.category = state.filters.category;
        }
        this.restoredCurrent = state.current;
        this.imageIndex = Number.isInteger(state.imageIndex) ? Math.max(0, state.imageIndex) : 0;
      } catch {}
    }
    persist() {
      try { localStorage.setItem(storageKey, JSON.stringify({history: this.history, filters: this.filters, seed: this.seed, current: this.current?.id, imageIndex: this.imageIndex})); } catch {}
    }
    previewUrl(path) {
      const url = new URL(path, window.location.origin);
      if (url.origin !== location.origin) return url.href;
      if (this.dataset.themeRole !== 'main') url.searchParams.set('preview_theme_id', this.dataset.themeId);
      return url.href;
    }
    updateLinks() {
      for (const link of this.querySelectorAll('.kd-header a,.kd-footer a')) link.href = this.previewUrl(link.getAttribute('href'));
      const cartLink = this.querySelector('[data-cart-link]');
      cartLink.href = this.previewUrl(cartLink.getAttribute('href'));
      const gridLink = this.querySelector('[data-grid-link]');
      const gridUrl = new URL(location.href);
      gridUrl.pathname = this.dataset.collectionUrl;
      for (const key of ['view', 'page', 'section_id']) gridUrl.searchParams.delete(key);
      gridLink.href = this.previewUrl(gridUrl.href);
    }
    merge(products) {
      this.products = Core.mergeProducts(this.products, products);
      this.dataset.productCount = this.products.length;
      this.updateFilterOptions();
      this.preloadNextProducts();
    }
    async loadCatalog() {
      if (this.loading) return;
      this.loading = true;
      this.catalogError = false;
      this.querySelector('[data-action="retry"]').hidden = true;
      try {
        const fetched = new Set();
        while (this.nextUrl && this.isConnected) {
          const url = new URL(this.nextUrl, location.origin);
          if (url.origin !== location.origin) throw new Error('Invalid catalog origin');
          url.pathname = this.dataset.collectionUrl || `${this.dataset.root}collections/all`;
          url.searchParams.delete('view');
          url.searchParams.set('section_id', 'kf-discovery-feed');
          if (this.dataset.themeRole !== 'main') url.searchParams.set('preview_theme_id', this.dataset.themeId);
          if (fetched.has(url.href)) throw new Error('Repeated catalog page');
          fetched.add(url.href);
          const response = await fetch(url, {credentials: 'same-origin', signal: AbortSignal.timeout(20000)});
          if (!response.ok) throw new Error('Catalog unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const node = doc.querySelector('[data-discovery-catalog]');
          if (!node) throw new Error('Catalog missing');
          const page = JSON.parse(node.textContent);
          this.merge(page.products);
          this.nextUrl = page.next;
          if (!this.current) this.chooseNext();
        }
        this.complete = !this.nextUrl;
        this.dataset.catalogComplete = String(this.complete);
        if (!this.current) this.chooseNext();
        if (this.status.textContent === this.strings.loading) this.message('');
      } catch (error) {
        this.catalogError = true;
        this.message(this.strings.load_error);
        this.querySelector('[data-action="retry"]').hidden = false;
      } finally { this.loading = false; this.renderControls(); }
    }
    async initWishlist() {
      if (this.wishlistLoading || this.app) return;
      this.wishlistLoading = true;
      try {
        const waitForApp = async () => {
          if (window.WishlistKing?.state) return window.WishlistKing;
          if (!window.WishlistKingAppLoaderURL) throw new Error('Wishlist unavailable');
          const {default: loader} = await import(window.WishlistKingAppLoaderURL);
          const {artifact} = await loader.waitForCore('wishlist-app');
          if (!artifact?.state) throw new Error('Wishlist not ready');
          return artifact;
        };
        this.app = await deadline(waitForApp());
        this.wishlistObserverReady = true;
        this.app.state.observeWishlist({wishlistId: 'mine'}).subscribe(wishlist => this.observeWishlistSnapshot(wishlist));
        await this.refreshWishlist();
        this.wishlistReady = true;
        this.dataset.wishlistReady = 'true';
        if (this.status.textContent === this.strings.save_error) this.message('');
      } catch {
        this.app = null;
        this.wishlistReady = false;
        this.dataset.wishlistReady = 'false';
        this.message(this.strings.save_error);
        this.querySelector('[data-action="retry"]').hidden = false;
      } finally { this.wishlistLoading = false; this.renderControls(); }
    }
    async refreshWishlist() {
      if (!this.app) return;
      if (this.wishlistRefreshPromise) return this.wishlistRefreshPromise;
      const revision = this.wishlistRevision || 0;
      const request = (async () => {
        // Swish invalidates its wishlist cache after writes; preserve its product cache.
        const result = await deadline(this.app.apis.wishlist.loadWishlist({wishlistId: 'mine'}));
        if (!result?.wishlist || !Array.isArray(result.wishlist.items) || result.wishlist.items.some(item => !this.isConfirmedWishlistItem(item))) throw new Error('Wishlist not confirmed');
        if (revision === (this.wishlistRevision || 0)) {
          this.wishlistItems = result.wishlist.items;
          this.renderControls();
          if (this.current) this.preloadNextProducts?.();
        }
      })();
      this.wishlistRefreshPromise = request;
      try { return await request; }
      finally { if (this.wishlistRefreshPromise === request) this.wishlistRefreshPromise = null; }
    }
    isConfirmedWishlistItem(item) { return !!item?.id && !String(item.id).startsWith('temp-'); }
    observeWishlistSnapshot(wishlist) {
      const write = this.wishlistWriteSync;
      if (Array.isArray(wishlist?.items) && wishlist.items.every(item => this.isConfirmedWishlistItem(item)) && !(write?.kind === 'remove' && !write.confirmed)) {
        this.wishlistObservedItems = wishlist.items;
        this.settleWishlistWrite();
      }
      if (this.isConnected && !this.busy) this.renderControls();
    }
    beginWishlistWrite(kind) {
      if (!this.wishlistObserverReady) return null;
      let resolve;
      const promise = new Promise(done => { resolve = done; });
      return this.wishlistWriteSync = {kind, promise, resolve, confirmed: false};
    }
    settleWishlistWrite() {
      const write = this.wishlistWriteSync;
      if (!write?.confirmed || !this.wishlistObservedItems || !write.matches(this.wishlistObservedItems)) return;
      // Swish clears its pending guard immediately after notifying this observer.
      Promise.resolve().then(() => {
        if (this.wishlistWriteSync === write) this.wishlistWriteSync = null;
        write.resolve();
      });
    }
    async waitForWishlistWrite() {
      const write = this.wishlistWriteSync;
      if (!write) return;
      try { await deadline(write.promise, 5000); }
      catch {
        // An unchanged SDK snapshot may not emit. Reconcile without repeating a write.
        await deadline(Promise.resolve(this.app.state?.revalidate?.()), 5000);
        await this.refreshWishlist();
        await Promise.resolve();
        if (this.wishlistWriteSync === write) this.wishlistWriteSync = null;
        write.resolve();
      }
    }
    savedItem(product = this.current) { return product?.handle ? this.wishlistItems.find(item => item.product?.handle === product.handle) : undefined; }
    async add(product, origin = 'details', analyticsContext = this.analyticsContext()) {
      await this.waitForWishlistWrite();
      await this.refreshWishlist();
      const existing = this.savedItem(product);
      if (existing) return {addedId: null};
      const write = this.beginWishlistWrite('add');
      try {
        const result = await this.app.addWishlistItem({productHandle: product.handle});
        if (!this.isConfirmedWishlistItem(result?.wishlistItem)) throw new Error('Save not confirmed');
        this.wishlistRevision = (this.wishlistRevision || 0) + 1;
        this.wishlistItems = [...this.wishlistItems.filter(item => item.id !== result.wishlistItem.id), {...result.wishlistItem, product: {...result.wishlistItem.product, handle: product.handle}}];
        this.renderWishlistCount();
        this.track('wishlist_changed', product, {operation: 'add', origin}, analyticsContext);
        if (write) {
          write.confirmed = true;
          write.matches = items => items.some(item => item.id === result.wishlistItem.id);
          this.settleWishlistWrite();
        }
        if (this.current) this.preloadNextProducts?.();
        return {addedId: result.wishlistItem.id};
      } catch (error) {
        if (this.wishlistWriteSync === write) this.wishlistWriteSync = null;
        write?.resolve();
        throw error;
      }
    }
    async remove(item, origin = 'details', product = this.current, analyticsContext = this.analyticsContext()) {
      await this.waitForWishlistWrite();
      const write = this.beginWishlistWrite('remove');
      try {
        const result = await this.app.removeWishlistItem({wishlistItemId: item.id});
        if (!this.isConfirmedWishlistItem(result?.wishlistItem) || result.wishlistItem.id !== item.id) throw new Error('Removal not confirmed');
        this.wishlistRevision = (this.wishlistRevision || 0) + 1;
        this.wishlistItems = this.wishlistItems.filter(saved => saved.id !== item.id);
        this.renderWishlistCount();
        this.track('wishlist_changed', product, {operation: 'remove', origin}, analyticsContext);
        if (write) {
          write.confirmed = true;
          write.matches = items => !items.some(saved => saved.id === item.id);
          this.settleWishlistWrite();
        }
        if (this.current) this.preloadNextProducts?.();
      } catch (error) {
        if (this.wishlistWriteSync === write) this.wishlistWriteSync = null;
        write?.resolve();
        throw error;
      }
    }
    chooseNext(restore = false, preferred = null) {
      if (!this.current && this.restoredCurrent) {
        const restored = this.products.find(p => p.id === this.restoredCurrent && Core.matches(p, this.filters) && !this.history[p.id]);
        if (restored) { this.current = restored; restore = true; this.restoredCurrent = null; }
        else if (!this.complete && !this.catalogError) { this.render(); return; }
        else this.restoredCurrent = null;
      }
      if (!this.current) {
        const prepared = preferred && this.products.find(product => product.id === preferred.id);
        this.current = prepared && !this.history[prepared.id] && Core.matches(prepared, this.filters) ? prepared :
          Core.rank(this.products, this.history, this.filters, this.seed, this.wishlistItems.map(i => i.product?.handle), this.recent)[0];
        if (!restore) this.imageIndex = 0;
      }
      this.render();
      this.persist();
    }
    render() {
      const product = this.current;
      if (this.messageProductId && this.messageProductId !== product?.id) this.message('');
      this.dataset.currentProduct = product?.id || '';
      this.card.setAttribute('aria-busy', String(!product && !this.complete));
      this.querySelector('.kd-empty').hidden = !!product || !this.complete;
      if (!product) {
        this.photo.removeAttribute('src');
        this.photo.removeAttribute('srcset');
        this.photo.alt = '';
        this.querySelector('[data-card-title]').textContent = '';
        this.querySelector('[data-card-product-link]').hidden = true;
        const hasMatches = this.products.some(p => Core.matches(p, this.filters));
        this.querySelector('[data-empty-text]').textContent = hasMatches ? this.strings.finished : this.strings.empty;
        this.renderControls();
        return;
      }
      this.querySelector('[data-card-title]').textContent = Core.shortProductTitle(product);
      this.querySelector('[data-card-product-link]').setAttribute('aria-label', `${this.strings.view_product}: ${product.title}`);
      this.querySelector('[data-card-product-link]').title = product.title;
      this.querySelector('[data-card-product-link]').hidden = false;
      const [, modelName = '', description = ''] = String(product.title || '').trim().match(/^(\S+)([\s\S]*)$/u) || [];
      this.querySelector('[data-detail-model]').textContent = modelName;
      this.querySelector('[data-detail-description]').textContent = description;
      this.querySelector('[data-detail-price]').textContent = `${product.priceVaries ? this.strings.from + ' ' : ''}${product.price}`;
      const productLink = this.querySelector('[data-product-link]');
      const url = new URL(this.previewUrl(product.url));
      if (this.filters.size) {
        const variant = product.variants.find(v => v.available && v.options[product.sizeIndex] === this.filters.size);
        if (variant) url.searchParams.set('variant', variant.id);
      }
      productLink.href = url.href;
      this.querySelector('[data-card-product-link]').href = url.href;
      this.renderImage();
      this.renderControls();
      this.preloadNextProducts();
    }
    nextProducts(kind, limit = 1) {
      if (!this.current || !this.products?.length) return [];
      const product = this.current;
      const savedHandles = this.wishlistItems.map(item => item.product?.handle).filter(Boolean);
      const handles = kind === 'like' ? [...new Set([...savedHandles, product.handle])] : savedHandles;
      return Core.rank(this.products, {...this.history, [product.id]: {kind}}, this.filters, this.seed,
        handles, [...this.recent, product.category].slice(-8)).slice(0, limit);
    }
    preloadProduct(product, priority = 'auto') {
      const image = product?.images?.[0];
      if (!image) return null;
      this.imageCache ||= new Map();
      let entry = this.imageCache.get(image.src);
      if (!entry) {
        const photo = new Image();
        photo.decoding = 'async';
        photo.fetchPriority = priority;
        this.setImage(photo, image);
        entry = {photo, ready: false};
        entry.loaded = photo.decode().then(() => { entry.ready = true; return true; }, () => false);
      } else if (priority === 'high') entry.photo.fetchPriority = priority;
      this.imageCache.delete(image.src);
      this.imageCache.set(image.src, entry);
      while (this.imageCache.size > 4) this.imageCache.delete(this.imageCache.keys().next().value);
      return entry;
    }
    preloadNextProducts() {
      if (!this.current || !this.products?.length) return;
      const branches = ['skip', 'like'].map(kind => this.nextProducts(kind, 2));
      const planned = new Set();
      for (let index = 0; index < 2; index++) {
        for (const products of branches) {
          const product = products[index];
          if (!product || planned.has(product.id)) continue;
          planned.add(product.id);
          this.preloadProduct(product, index === 0 ? 'auto' : 'low');
        }
      }
    }
    setImage(element, image, detail = false) {
      const width = image.width || 2000;
      const sources = [[image.small, Math.min(800, width)], [image.medium, Math.min(1200, width)], [image.large, Math.min(1600, width)], [image.src, Math.min(2000, width)]];
      element.sizes = detail ? '(max-width: 600px) 94vw, 480px' : '(max-width: 600px) 100vw, 460px';
      element.srcset = [...new Map(sources.filter(([url]) => url).map(([url, size]) => [size, `${url} ${size}w`])).values()].join(', ');
      element.src = image.src;
      element.alt = image.alt || this.current?.title || '';
    }
    renderImage() {
      if (!this.current) return;
      const images = this.current.images;
      this.imageIndex = ((this.imageIndex % images.length) + images.length) % images.length;
      const image = images[this.imageIndex];
      this.setImage(this.photo, image);
      this.querySelector('[data-image-count]').textContent = `${this.imageIndex + 1} / ${images.length}`;
      this.querySelector('.kd-gallery-controls').hidden = images.length < 2;
      this.querySelector('.kd-detail-gallery').hidden = images.length < 2;
      if (this.detailDialog.open) {
        const detailPhoto = this.querySelector('.kd-detail-image img');
        this.setImage(detailPhoto, image, true);
      }
      this.trackView();
    }
    renderWishlistCount() {
      const badge = this.querySelector('[data-wishlist-count]');
      const link = this.querySelector('.kd-wishlist-link');
      if (!badge || !link) return;
      badge.hidden = !this.wishlistReady;
      if (!this.wishlistReady) return;
      const count = new Set(this.wishlistItems.filter(item => this.isConfirmedWishlistItem(item)).map(item => String(item.id))).size;
      badge.hidden = count === 0;
      const value = String(count);
      if (badge.textContent !== value) badge.textContent = value;
      link.setAttribute('aria-label', `${this.strings.wishlist}: ${value}`);
    }
    renderControls() {
      this.renderWishlistCount();
      const disabled = this.busy || !this.current;
      this.querySelector('[data-action="skip"]').disabled = disabled;
      this.querySelector('[data-action="like"]').disabled = disabled || !this.wishlistReady;
      this.querySelector('[data-action="undo"]').disabled = this.busy || !this.lastAction;
      for (const button of this.querySelectorAll('[data-action="details"],[data-action="next-image"],[data-action="previous-image"]')) button.disabled = disabled;
      for (const button of this.querySelectorAll('[data-action="filters"],[data-action="apply"],[data-action="reset-filters"]')) button.disabled = this.busy;
      this.querySelector('[data-action="detail-save"]').disabled = disabled || !this.wishlistReady;
      for (const button of this.querySelectorAll('[data-action="cart-options"]')) {
        button.disabled = disabled || !this.current?.variants.some(variant => variant.available);
        const added = this.current?.variants.some(variant => this.cartOutcomes?.get(variant.id) === 'added');
        button.classList.toggle('is-added', !!added);
        button.setAttribute('aria-label', added ? `${this.strings.cart_added}. ${this.strings.add_to_cart}` : this.strings.add_to_cart);
        button.title = added ? this.strings.cart_added : this.strings.add_to_cart;
      }
      const saved = !!this.savedItem();
      this.querySelector('[data-action="like"]').classList.toggle('is-saved', saved);
      this.querySelector('[data-action="like"]').setAttribute('aria-label', saved ? this.strings.saved : this.strings.save);
      this.querySelector('[data-detail-save-label]').textContent = saved ? this.strings.remove : this.strings.save;
      this.querySelector('[data-filter-summary]').textContent = this.strings[this.filters.category] || this.strings.all;
      this.querySelector('[data-action="filters"]').classList.toggle('is-filtered', !!this.filters.category);
      this.querySelector('.kd-help').classList.toggle('is-quiet', Object.keys(this.history).length >= 3);
      this.trackView();
    }
    updateFilterOptions() {
      if (!this.filterDialog || this.filterDialog.open) return;
      const categories = new Set(this.products.filter(p => p.available).map(p => p.category));
      this.querySelector('[data-filter="category"]').value = this.filters.category;
      for (const button of this.querySelectorAll('[data-action="category"]')) {
        const category = button.dataset.category;
        button.hidden = !!category && !categories.has(category) && category !== this.filters.category;
        button.setAttribute('aria-pressed', String(category === this.filters.category));
      }
      const more = this.querySelector('[data-more-categories]');
      const secondary = [...more.querySelectorAll('[data-action="category"]')];
      more.hidden = !secondary.some(button => !button.hidden);
      more.open = secondary.some(button => button.dataset.category === this.filters.category);
    }
    async click(event) {
      const target = event.target.closest('[data-action]');
      if (!target || target.disabled) return;
      const action = target.dataset.action;
      if (action === 'skip' || action === 'like') return this.decide(action, 'button');
      if (action === 'undo') return this.undo();
      if (action === 'details') return this.openDetails();
      if (action === 'cart-options') return this.openCartOptions();
      if (action === 'cart-add') return this.addToCart();
      if (action === 'close') return target.closest('dialog').close();
      if (action === 'next-image' || action === 'previous-image') return this.changeImage(action === 'next-image' ? 1 : -1);
      if (action === 'detail-save') return this.toggleDetailSave();
      if (action === 'filters') { this.updateFilterOptions(); this.filterDialog.showModal(); }
      if (action === 'category') {
        this.querySelector('[data-filter="category"]').value = target.dataset.category;
        for (const button of this.querySelectorAll('[data-action="category"]')) {
          button.setAttribute('aria-pressed', String(button === target));
        }
      }
      if (action === 'apply') {
        this.filters = {...defaults(), category: this.querySelector('[data-filter="category"]').value};
        this.filterDialog.close();
        this.current = null;
        this.lastAction = null;
        this.message('');
        this.chooseNext();
      }
      if (action === 'reset-filters') {
        this.filters = defaults();
        this.filterDialog.close();
        this.updateFilterOptions();
        this.current = null;
        this.lastAction = null;
        this.chooseNext();
      }
      if (action === 'retry') { await this.initWishlist(); this.loadCatalog(); }
      if (action === 'restart') {
        const hasMatches = this.products.some(p => Core.matches(p, this.filters));
        if (!hasMatches) this.filters = defaults();
        else for (const product of this.products.filter(p => Core.matches(p, this.filters))) delete this.history[product.id];
        this.seed = String(crypto.getRandomValues(new Uint32Array(1))[0]);
        this.current = null;
        this.lastAction = null;
        this.chooseNext();
      }
    }
    openDetails() {
      if (!this.current || this.busy) return;
      this.querySelector('.kd-detail-status').textContent = '';
      this.detailDialog.showModal();
      this.renderImage();
      this.track('detail_opened', this.current);
    }
    openCartOptions() {
      if (!this.current || this.busy) return;
      const product = this.current;
      if (this.detailDialog.open) this.detailDialog.close();
      this.querySelector('[data-cart-title]').textContent = product.title;
      const image = this.querySelector('[data-cart-image]');
      image.src = product.images[this.imageIndex]?.small || product.images[0].small;
      image.alt = product.title;
      const select = this.querySelector('[data-cart-variant]');
      select.replaceChildren(new Option(this.strings.choose_variant, ''));
      for (const variant of product.variants) {
        const option = new Option(`${variant.options.join(' / ')}${variant.available ? '' : ' · ' + this.strings.sold_out}`, variant.id);
        option.disabled = !variant.available;
        select.add(option);
      }
      const available = product.variants.filter(variant => variant.available);
      const matching = this.filters.size && available.filter(variant => variant.options[product.sizeIndex] === this.filters.size);
      if (matching?.length === 1) select.value = matching[0].id;
      else if (product.variants.length === 1 && available.length === 1) select.value = available[0].id;
      this.renderCartSelection();
      this.cartDialog.showModal();
    }
    selectedCartVariant() {
      const value = this.querySelector('[data-cart-variant]').value;
      return this.current?.variants.find(variant => variant.id === value && variant.available);
    }
    renderCartSelection() {
      const variant = this.selectedCartVariant();
      const outcome = this.cartOutcomes.get(variant?.id);
      const button = this.querySelector('[data-action="cart-add"]');
      button.disabled = this.busy || !variant || !!outcome;
      const label = this.busy ? this.strings.cart_adding : outcome === 'added' ? this.strings.cart_added : outcome ? this.strings.cart_check : this.strings.add_to_cart;
      button.querySelector('[data-cart-button-label]').textContent = label;
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-busy', String(this.busy));
      button.classList.toggle('is-added', outcome === 'added');
      button.classList.toggle('is-adding', this.busy);
      const status = this.querySelector('.kd-cart-status');
      status.classList.toggle('kd-sr', outcome === 'added');
      status.textContent = outcome === 'added' ? this.strings.cart_added : outcome === 'uncertain' ? this.strings.cart_uncertain : outcome ? this.strings.cart_error : '';
      this.querySelector('[data-cart-link]').hidden = !outcome;
      this.querySelector('[data-cart-variant]').disabled = this.busy;
      this.querySelector('[data-cart-price]').textContent = variant?.price || `${this.current.priceVaries ? this.strings.from + ' ' : ''}${this.current.price}`;
      const url = new URL(this.previewUrl(this.current.url));
      if (variant) url.searchParams.set('variant', variant.id);
      this.querySelector('[data-cart-product-link]').href = url.href;
    }
    animateCartConfirmation() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      for (const button of this.querySelectorAll('[data-action="cart-add"].is-added,[data-action="cart-options"].is-added')) {
        button.querySelector('.kd-cart-bag')?.animate([
          {transform: 'rotate(0deg)'}, {transform: 'rotate(-14deg)', offset: .2},
          {transform: 'rotate(12deg)', offset: .45}, {transform: 'rotate(-7deg)', offset: .7},
          {transform: 'rotate(0deg)'}
        ], {duration: 520, easing: 'ease-in-out'});
        button.querySelector('.kd-cart-check')?.animate([
          {opacity: 0, transform: 'scale(.4)'}, {opacity: 1, transform: 'scale(1.15)', offset: .7},
          {opacity: 1, transform: 'scale(1)'}
        ], {duration: 240, delay: 240, fill: 'backwards', easing: 'ease-out'});
      }
    }
    async addToCart() {
      if (this.busy || !this.current) return;
      const variant = this.selectedCartVariant();
      if (!variant || this.cartOutcomes.has(variant.id)) return;
      const product = this.current, analyticsContext = this.analyticsContext();
      this.busy = true;
      this.renderControls();
      this.renderCartSelection();
      try {
        const response = await fetch(`${(this.dataset.root || '/').replace(/\/?$/, '/')}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items: [{id: variant.id, quantity: 1}]}),
          signal: AbortSignal.timeout(20000)
        });
        if (!response.ok) {
          this.cartOutcomes.set(variant.id, response.status >= 500 ? 'uncertain' : 'error');
          return;
        }
        const result = await response.json();
        const confirmed = result.items?.some(item => String(item.variant_id || item.id) === variant.id && item.quantity > 0);
        this.cartOutcomes.set(variant.id, confirmed ? 'added' : 'uncertain');
        if (confirmed) this.track('cart_added', product, {variant_id: String(variant.id), quantity: 1}, analyticsContext);
      } catch {
        this.cartOutcomes.set(variant.id, 'uncertain');
      } finally {
        this.busy = false;
        this.renderControls();
        this.renderCartSelection();
        if (this.cartOutcomes.get(variant.id) === 'added') this.animateCartConfirmation();
      }
    }
    changeImage(delta) { if (this.current && !this.busy) { this.imageIndex += delta; this.renderImage(); this.persist(); } }
    async waitForCardImage() {
      if (!this.current) return true;
      try {
        await deadline(this.photo.decode(), 6000);
        this.photo.style.visibility = '';
        return true;
      } catch {
        this.photo.style.visibility = 'hidden';
        this.message(this.strings.image_error, this.current?.id);
        return false;
      }
    }
    async decide(kind, input = 'button') {
      if (this.busy || !this.current || (kind === 'like' && !this.wishlistReady)) return;
      this.busy = true;
      this.renderControls();
      const product = this.current;
      const imageIndex = this.imageIndex;
      const analyticsContext = this.analyticsContext();
      const nextProduct = this.nextProducts(kind)[0];
      this.preloadProduct(nextProduct, 'high');
      let animation;
      const save = (kind === 'like' ? this.add(product, 'swipe', analyticsContext) : Promise.resolve({addedId: null}))
        .then(result => ({result}), error => ({error}));
      try {
        if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
          animation = this.card.animate([{transform: this.card.style.transform || 'translate3d(0,0,0) rotate(0deg)', opacity: 1},{transform: `translate3d(${kind === 'like' ? '' : '-'}110%,0,0) rotate(${kind === 'like' ? '' : '-'}8deg)`, opacity: 0}], {duration: 210, easing: 'cubic-bezier(.2,.65,.3,1)', fill: 'forwards'});
          this.exitAnimation = animation;
        }
        const [outcome] = await Promise.all([save, animation?.finished.catch(() => {})]);
        if (outcome.error) throw outcome.error;
        const {addedId} = outcome.result;
        this.lastAction = {product, imageIndex, addedId, previous: this.history[product.id]};
        this.history[product.id] = {kind, at: Date.now()};
        const analyticsEventId = this.track('decision', product, {kind, input, saved_new: !!addedId, image_index: imageIndex}, analyticsContext);
        if (analyticsEventId) Object.assign(this.lastAction, {analyticsEventId, analyticsContext});
        this.recent.push(product.category);
        this.recent = this.recent.slice(-8);
        this.card.style.visibility = 'hidden';
        this.current = null;
        this.chooseNext(false, nextProduct);
        if (await this.waitForCardImage()) this.message('');
      } catch {
        animation?.cancel();
        this.exitAnimation = null;
        this.returnCard();
        this.message(this.strings.save_error, product.id);
      } finally {
        animation?.cancel();
        this.exitAnimation = null;
        this.resetDrag();
        this.card.style.visibility = '';
        this.busy = false;
        this.renderControls();
      }
    }
    async undo() {
      if (this.busy || !this.lastAction) return;
      this.busy = true;
      this.renderControls();
      const action = this.lastAction;
      const analyticsContext = this.analyticsContext();
      try {
        if (action.addedId) {
          await this.refreshWishlist();
          const item = this.wishlistItems.find(item => item.id === action.addedId);
          if (item) await this.remove(item, 'undo', action.product, analyticsContext);
        }
        if (action.previous) this.history[action.product.id] = action.previous;
        else delete this.history[action.product.id];
        this.current = action.product;
        this.imageIndex = action.imageIndex;
        this.lastAction = null;
        this.recent.pop();
        this.render();
        this.persist();
        this.message(this.strings.undo_done, action.product.id);
        if (action.analyticsEventId) this.track('decision_undone', action.product, {original_event_id: action.analyticsEventId}, action.analyticsContext);
      } catch { this.message(this.strings.save_error, this.current?.id); }
      finally { this.busy = false; this.renderControls(); }
    }
    async toggleDetailSave() {
      if (this.busy || !this.current || !this.wishlistReady) return;
      this.busy = true;
      this.renderControls();
      const analyticsContext = this.analyticsContext();
      try {
        await this.refreshWishlist();
        const item = this.savedItem();
        if (item) { await this.remove(item, 'details', this.current, analyticsContext); this.message(this.strings.removed, this.current.id); }
        else { await this.add(this.current, 'details', analyticsContext); this.message(this.strings.saved, this.current.id); }
      } catch { this.message(this.strings.save_error, this.current?.id); }
      finally { this.busy = false; this.renderControls(); }
    }
    pointerDown(event) {
      if (event.isPrimary === false || this.drag) { this.pointerCancel(); return; }
      if (this.busy || this.returnAnimation || !this.current || event.button !== 0 || this.detailDialog.open || this.filterDialog.open || this.cartDialog?.open) return;
      this.drag = {id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, dy: 0, horizontal: false, moved: false, samples: [{x: event.clientX, time: event.timeStamp}]};
      this.card.setPointerCapture(event.pointerId);
    }
    updateDrag(event) {
      const drag = this.drag;
      drag.dx = event.clientX - drag.x;
      drag.dy = event.clientY - drag.y;
      drag.moved ||= Math.max(Math.abs(drag.dx), Math.abs(drag.dy)) >= 8;
      drag.samples.push({x: event.clientX, time: event.timeStamp});
      drag.samples = drag.samples.filter(point => event.timeStamp - point.time <= 120);
      if (Math.abs(drag.dx) > 10 && Math.abs(drag.dx) > Math.abs(drag.dy) * 1.1) drag.horizontal = true;
      return drag;
    }
    dragTransform(drag) { return `translate3d(${drag.dx}px,0,0) rotate(${Math.max(-8, Math.min(8, drag.dx / 28))}deg)`; }
    pointerMove(event) {
      if (!this.drag || this.drag.id !== event.pointerId) return;
      const drag = this.updateDrag(event);
      if (!drag.horizontal && Math.abs(drag.dy) > 12 && Math.abs(drag.dy) > Math.abs(drag.dx)) return this.returnCard();
      if (!drag.horizontal) return;
      event.preventDefault();
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.drag !== drag) return;
        this.card.classList.add('is-dragging');
        this.card.style.transform = this.dragTransform(drag);
        this.querySelector('.kd-swipe-label--like').style.opacity = drag.dx > 0 ? Math.min(drag.dx / 90, 1) : 0;
        this.querySelector('.kd-swipe-label--skip').style.opacity = drag.dx < 0 ? Math.min(-drag.dx / 90, 1) : 0;
      });
    }
    pointerUp(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const drag = this.updateDrag(event);
      const threshold = Math.min(72, this.card.clientWidth * .18);
      const first = drag.samples[0], last = drag.samples[drag.samples.length - 1];
      const velocity = first && last && last.time > first.time ? (last.x - first.x) / (last.time - first.time) : 0;
      const flick = Math.abs(drag.dx) >= 32 && Math.abs(velocity) >= .5 && Math.sign(velocity) === Math.sign(drag.dx);
      const kind = drag.dx > 0 ? 'like' : 'skip';
      const committed = drag.horizontal && Math.abs(drag.dx) > Math.abs(drag.dy) && (Math.abs(drag.dx) >= threshold || flick) && (kind !== 'like' || this.wishlistReady);
      if (committed) {
        this.card.style.transform = this.dragTransform(drag);
        this.resetDrag(true);
        this.decide(kind, event.pointerType === 'touch' ? 'touch' : 'button');
      } else {
        this.returnCard();
        if (!drag.moved) this.changeImage(1);
      }
    }
    pointerCancel() { if (this.drag) this.returnCard(); }
    returnCard() {
      const transform = this.card.style.transform;
      this.resetDrag();
      if (!transform || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.returnAnimation?.cancel();
      const animation = this.card.animate([{transform}, {transform: 'translate3d(0,0,0) rotate(0deg)'}], {duration: 160, easing: 'cubic-bezier(.22,1,.36,1)'});
      this.returnAnimation = animation;
      animation.finished.catch(() => {}).finally(() => { if (this.returnAnimation === animation) this.returnAnimation = null; });
    }
    resetDrag(preserveTransform = false) {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      const drag = this.drag;
      this.drag = null;
      if (drag && this.card.hasPointerCapture(drag.id)) this.card.releasePointerCapture(drag.id);
      if (!preserveTransform) this.card.style.transform = '';
      this.card.classList.remove('is-dragging');
      for (const label of this.querySelectorAll('.kd-swipe-label')) label.style.opacity = 0;
    }
  }
  customElements.define('kf-discovery', KultfrauDiscovery);
})();
